import {
	getImageInfo,
	updateImageInfo,
	getImageInfoRecords,
	getCameraModel,
} from "@/store/processing/image-info"
import {
	allPhotosAtom,
	cullingInfoAtom,
	getPhotoMetadata,
	photosAtom,
	updateCullingInfos,
} from "@/store/photo/photos-state"
import { store } from "@/store/store"
import type {
	CullingInfoRecord,
	ImageInfoRecord,
	PhotoRecord,
	PhotoMetadataRecord,
} from "../catalog/types"
import {
	getDeepClusterDistance,
	getDeepClusterDistanceForCulling,
	getMetaDistance,
	getOverallLightDistance,
	getSceneThreshDelta,
	getWBDistance,
	getWBDistanceFromMetadata,
	saturationBiasBW,
} from "@/lib/ai/calibration/ai-calibration-feature"
import { meanAbsoluteDifference } from "@/lib/ai/calibration/ai-calibration-utils"
import { logClustering } from "./utils"
import {
	sceneDThreshBW,
	sceneDThreshForVerySimilarNeighbor,
} from "@/lib/ai/calibration/ai-calibration-keyadj"
import { getFileFormat } from "@/utils/file"
import { didFlashFire } from "@/utils/exif"
import { clamp, dist } from "@/utils/math"
import { ClusterThreshold } from "@/lib/catalog/types"
import {
	blurryPhotoThresholdAtom,
	poorFaceQualityThresholdAtom,
	rejectBlurryPhotosAtom,
	rejectPoorExposureAtom,
	rejectPoorFaceQualityAtom,
} from "@/store/importing/culling-state"
import { cached } from "@/utils/cached"
import { projectAtom } from "@/store/project/projects-state"
import { isMetadataFinishedAtom } from "@/store/app-state"
import { deltaSceneThreshAtom, deltaTimeThreshAtom } from "@/store/settings-state"

/** Algorithm thresholds — can be changed/tuned without affecting stored data.
 * Maps storage keys (ClusterThreshold enum values) to actual threshold values used in clustering. */
export const ClusterThresholdValues: Record<ClusterThreshold, number> = {
	[ClusterThreshold.Loose]: 0.22,
	[ClusterThreshold.Similar]: 0.16,
	[ClusterThreshold.Exact]: 0.12,
	[ClusterThreshold.EditClustering]: 0.235,
	[ClusterThreshold.VerySimilar]: sceneDThreshForVerySimilarNeighbor,
	[ClusterThreshold.EditClusteringFinal]: 1, // Marker only, not used for actual clustering
}

const CullingClusterThresholds = [
	ClusterThreshold.Similar,
	ClusterThreshold.Loose,
	ClusterThreshold.Exact,
] as const

/** Get the actual threshold value for a given clustering level. */
export function getClusterThresholdValue(threshold: ClusterThreshold): number {
	return ClusterThresholdValues[threshold]
}

export interface Clusters {
	[keyPhotoID: string]: string[]
}

/** Build a cluster map (keyPhotoId → memberIds[]) from photos using each photo's stored keyNeighborId. */
export function generateClusters(photos: Record<string, PhotoRecord>) {
	const clusters: Clusters = {}

	for (const photo of Object.values(photos)) {
		const imageInfo = getImageInfo(photo.id)
		const keyNeighborId = imageInfo?.keyNeighborId ?? photo.id
		clusters[keyNeighborId] = clusters[keyNeighborId] || []
		clusters[keyNeighborId]?.push(photo.id)
	}

	return clusters
}

/** Variant of generateClusters that takes a pre-fetched imageInfos map instead of reading from
 * the store — used during atom derivation to avoid store reads inside derived atoms. */
export function generateClustersFromImageInfoAtom(
	photos: Record<string, PhotoRecord>,
	imageInfos: Record<string, ImageInfoRecord>,
) {
	const clusters: Clusters = {}

	for (const photo of Object.values(photos)) {
		const imageInfo = imageInfos[photo.id]
		const keyNeighborId = imageInfo?.keyNeighborId ?? photo.id
		clusters[keyNeighborId] = clusters[keyNeighborId] || []
		clusters[keyNeighborId]?.push(photo.id)
	}

	return clusters
}

function getMemberInfos(photos: Record<string, PhotoRecord>, keyId: string) {
	const memberInfos: Record<string, ImageInfoRecord> = {}
	for (const id in photos) {
		const imageInfo = getImageInfo(id)
		if (imageInfo?.keyNeighborId === keyId) {
			memberInfos[id] = imageInfo
		}
	}
	return memberInfos
}

/** When a reference photo is approved inside an existing cluster, redistribute cluster members
 * to the newly approved photo as key photo — with special B&W handling and cluster splits when needed. */
export function makeApprovedPhotoKeyNeighbor(
	approvedInfo: ImageInfoRecord,
	prevKeyID: string | null,
	isBlackAndWhite: boolean,
) {
	const keyNeighborId = approvedInfo.imageId

	// Make black&white reference keyNeighbor only if sceneDist is smaller than sceneDThreshBW
	if (isBlackAndWhite && prevKeyID) {
		const photos = store.get(photosAtom)
		const memberInfos = getMemberInfos(photos, prevKeyID)

		let otherKeyNeighborId: string | undefined = undefined
		let prevKeyInfo: ImageInfoRecord | undefined = undefined
		if (prevKeyID !== keyNeighborId && photos[prevKeyID]?.kind == "reference") {
			prevKeyInfo = getImageInfo(prevKeyID)
			otherKeyNeighborId = prevKeyID
		}
		for (const [memberID, imageInfo] of Object.entries(memberInfos)) {
			if (memberID === keyNeighborId) {
				continue
			}

			const sceneDistCurr = getDeepClusterDistance(imageInfo, approvedInfo)
			const sceneDistOld = prevKeyInfo ? getDeepClusterDistance(imageInfo, prevKeyInfo) : 100

			if (
				memberID !== otherKeyNeighborId &&
				sceneDistCurr < sceneDistOld &&
				sceneDistCurr < sceneDThreshBW
			) {
				if (keyNeighborId !== imageInfo.keyNeighborId) {
					const newInfo: ImageInfoRecord = { ...imageInfo, keyNeighborId: keyNeighborId }
					updateImageInfo(newInfo)
				}
			} else {
				otherKeyNeighborId = otherKeyNeighborId ?? imageInfo.imageId
				if (otherKeyNeighborId !== imageInfo.keyNeighborId) {
					const newInfo: ImageInfoRecord = {
						...imageInfo,
						keyNeighborId: otherKeyNeighborId,
					}
					updateImageInfo(newInfo)
				}
			}
		}
	} else if (prevKeyID && prevKeyID !== keyNeighborId) {
		const photos = store.get(photosAtom)
		const memberInfos = getMemberInfos(photos, prevKeyID)

		// If the newly approved photo is a second approved photo inside the cluster, need to split the cluster
		let doSplitting = photos[prevKeyID]?.kind === "reference"

		// If the approved photo has no face, but prevKey has face, do splitting if needed
		const hasFace = approvedInfo.features.light.faceLum > 0
		const prevKeyInfo = getImageInfo(prevKeyID)
		const prevKeyFeatures = prevKeyInfo?.features
		if (!doSplitting && !hasFace && prevKeyFeatures && prevKeyFeatures.light.faceLum) {
			const sceneDist = getDeepClusterDistance(prevKeyInfo, approvedInfo)
			if (sceneDist > 0.245) {
				doSplitting = true
			}
		}

		if (prevKeyInfo && doSplitting) {
			for (const [memberID, imageInfo] of Object.entries(memberInfos)) {
				if (memberID === keyNeighborId || memberID === prevKeyID) {
					continue
				}
				const sceneDistCurr = getDeepClusterDistance(imageInfo, approvedInfo)
				const sceneDistOld = getDeepClusterDistance(imageInfo, prevKeyInfo)
				if (sceneDistCurr < sceneDistOld) {
					const newInfo: ImageInfoRecord = { ...imageInfo, keyNeighborId: keyNeighborId }
					updateImageInfo(newInfo)
				}
			}
		} else {
			for (const [memberID, imageInfo] of Object.entries(memberInfos)) {
				if (memberID === keyNeighborId) {
					continue
				}
				const newInfo: ImageInfoRecord = { ...imageInfo, keyNeighborId: keyNeighborId }
				updateImageInfo(newInfo)
			}
		}
	}
}

function isValidCandidateForKeyPhoto(photo: PhotoRecord) {
	return (
		(!photo.isHidden && !photo.isUnsupported && photo.kind != "locked") ||
		photo.kind === "reference"
	)
}

/** Ensure hidden or locked photo is not the key photo in its cluster. */
export function ensurePhotoNotKeyNeighbor(photoId: string, clusters: Clusters | null = null) {
	const thisPhotoInfo = getImageInfo(photoId)
	if (!thisPhotoInfo) return clusters

	const prevKeyID = thisPhotoInfo.keyNeighborId ?? thisPhotoInfo.imageId
	if (prevKeyID !== thisPhotoInfo.imageId) {
		return clusters
	}

	const photos = store.get(photosAtom)
	const thisPhoto = photos[photoId]
	if (thisPhoto && isValidCandidateForKeyPhoto(thisPhoto)) return clusters

	if (!clusters) clusters = generateClusters(photos)

	let newKeyNeighborId: string | null = null

	// Go through unhidden and unlocked members to find the newKeyNeighborId
	const memberIds = clusters[prevKeyID]
	if (memberIds) {
		for (const id of memberIds) {
			const photo = photos[id]
			if (photo && isValidCandidateForKeyPhoto(photo)) {
				newKeyNeighborId = id
				break
			}
		}
	}

	logClustering("ensurePhotoNotKeyNeighbor newKey:", newKeyNeighborId)

	// If newKeyNeighborId exists, meaning there is at least one unHidden/unLocked photo in this cluster, update all photos' keyNeighbor
	if (newKeyNeighborId && memberIds) {
		delete clusters[prevKeyID]

		for (const id of memberIds) {
			const imageInfo = getImageInfo(id)
			if (!imageInfo) continue

			const newInfo: ImageInfoRecord = { ...imageInfo, keyNeighborId: newKeyNeighborId }
			updateImageInfo(newInfo)

			clusters[newKeyNeighborId] = clusters[newKeyNeighborId] || []
			clusters[newKeyNeighborId]?.push(id)
		}
	}

	return clusters
}

/** If the current key photo is being hidden or locked, pick the first valid member as the
 * replacement key and update all cluster members. Returns the effective key ID (may be unchanged). */
export function reAssignKeyPhotoIfNeeded(keyPhoto: PhotoRecord, memberIds: string[]) {
	if (isValidCandidateForKeyPhoto(keyPhoto) || memberIds.length <= 1) return keyPhoto.id

	const photos = store.get(photosAtom)

	let newKeyNeighborId: string | null = null

	// Go through unhidden and unlocked members to find the newKeyNeighborId
	if (memberIds) {
		for (const id of memberIds) {
			const photo = photos[id]
			if (photo && isValidCandidateForKeyPhoto(photo)) {
				newKeyNeighborId = id
				break
			}
		}
	}

	if (newKeyNeighborId) {
		for (const id of memberIds) {
			const imageInfo = getImageInfo(id)
			if (!imageInfo) continue

			const newInfo: ImageInfoRecord = { ...imageInfo, keyNeighborId: newKeyNeighborId }
			updateImageInfo(newInfo)
		}
	}

	return newKeyNeighborId ?? keyPhoto.id
}

/** Called when a photo becomes valid (e.g., unhidden/unlocked). If its current cluster key is
 * invalid, promote this photo to be the new key for all cluster members. */
export function makePhotoKeyNeighborIfNeeded(photoId: string, clusters: Clusters | null = null) {
	const thisPhotoInfo = getImageInfo(photoId)
	if (!thisPhotoInfo) return clusters

	const prevKeyID = thisPhotoInfo.keyNeighborId ?? thisPhotoInfo.imageId
	if (prevKeyID === thisPhotoInfo.imageId) {
		return clusters
	}

	const photos = store.get(photosAtom)
	const prevKeyPhoto = photos[prevKeyID]

	// prev key photo is already unhidden and unlocked
	if (!prevKeyPhoto || isValidCandidateForKeyPhoto(prevKeyPhoto)) {
		return clusters
	}

	logClustering("makePhotoKeyNeighborIfNeeded newKey:", photoId)

	if (!clusters) clusters = generateClusters(photos)

	// prev key photos is invalid (hidden/locked), update all members' key photo
	const newKeyNeighborId: string = photoId
	const memberIds = clusters[prevKeyID]
	if (memberIds) {
		delete clusters[prevKeyID]

		for (const id of memberIds) {
			const imageInfo = getImageInfo(id)
			if (!imageInfo) continue

			const newInfo: ImageInfoRecord = { ...imageInfo, keyNeighborId: newKeyNeighborId }
			updateImageInfo(newInfo)

			clusters[newKeyNeighborId] = clusters[newKeyNeighborId] || []
			clusters[newKeyNeighborId]?.push(id)
		}
	}

	return clusters
}

/**
 * Ensure a hidden or locked photo is not the cluster leader in cullingInfo's
 * clusterLeaderIdMap for EditClustering.
 * @param photoId - The photo that became hidden or locked
 * @param clusters - Optional pre-computed clusters to avoid repeated computation
 * @returns Updated clusters for reuse
 */
export function ensureCullingClusterLeaderNotInvalid(
	photoId: string,
	clusters: Clusters | null = null,
): Clusters | null {
	const photos = store.get(photosAtom)
	const thisPhoto = photos[photoId]
	if (!thisPhoto) return clusters

	const allCullingInfo = store.get(cullingInfoAtom)
	const thisCullingInfo = allCullingInfo[photoId]
	if (!thisCullingInfo) return clusters

	const threshold = ClusterThreshold.EditClustering

	// Check if this photo is the leader for EditClustering
	const leaderId = thisCullingInfo.clusterLeaderIdMap?.[threshold] ?? photoId
	if (leaderId !== photoId) {
		// This photo is not the leader
		return clusters
	}

	// Get or generate clusters
	if (!clusters) {
		clusters = generateCullingClusters(allCullingInfo, threshold)
	}

	const memberIds = clusters[photoId]
	if (!memberIds || memberIds.length <= 1) return clusters

	// Find a new leader (unhidden, not locked), preferring earlier photos in sort order
	const { idToIndex } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))
	memberIds.sort((a, b) => (idToIndex[a] ?? 0) - (idToIndex[b] ?? 0))

	let newLeaderId: string | null = null
	for (const id of memberIds) {
		if (id === photoId) continue
		const photo = photos[id]
		if (photo && isValidCandidateForKeyPhoto(photo)) {
			newLeaderId = id
			break
		}
	}

	if (!newLeaderId) return clusters

	// Capture for closure - TypeScript needs this for type narrowing
	const newLeader = newLeaderId

	logClustering(`ensureCullingClusterLeaderNotInvalid: ${photoId} -> ${newLeader}`)

	// Update clusters map
	delete clusters[photoId]
	clusters[newLeader] = memberIds

	// Update all members' clusterLeaderIdMap to point to new leader
	updateCullingInfos((infos) => {
		for (const id of memberIds) {
			const info = allCullingInfo[id]
			if (!info) continue
			infos[id] = {
				...info,
				clusterLeaderIdMap: { ...info.clusterLeaderIdMap, [threshold]: newLeader },
			}
		}
	})

	return clusters
}

/**
 * Make this photo the cluster leader if the current leader is hidden (for EditClustering).
 * Called when unhiding a photo - if the current cluster leader is hidden, make this photo the new leader.
 * @param photoId - The photo being unhidden
 * @param clusters - Optional pre-computed clusters to avoid repeated computation
 * @returns Updated clusters for reuse
 */
export function makeCullingClusterLeaderIfNeeded(
	photoId: string,
	clusters: Clusters | null = null,
): Clusters | null {
	const photos = store.get(photosAtom)
	const thisPhoto = photos[photoId]
	if (!thisPhoto) return clusters

	const allCullingInfo = store.get(cullingInfoAtom)
	const thisCullingInfo = allCullingInfo[photoId]
	if (!thisCullingInfo) return clusters

	const threshold = ClusterThreshold.EditClustering

	const currentLeaderId = thisCullingInfo.clusterLeaderIdMap?.[threshold]
	if (!currentLeaderId || currentLeaderId === photoId) {
		// This photo is already the leader or not part of a cluster
		return clusters
	}

	// Check if current leader is hidden/locked
	const currentLeaderPhoto = photos[currentLeaderId]
	if (currentLeaderPhoto && isValidCandidateForKeyPhoto(currentLeaderPhoto)) {
		// Current leader is valid, no need to change
		return clusters
	}

	// Current leader is invalid, make this photo the new leader
	logClustering(`makeCullingClusterLeaderIfNeeded: ${currentLeaderId} -> ${photoId}`)

	// Get or generate clusters
	if (!clusters) {
		clusters = generateCullingClusters(allCullingInfo, threshold)
	}

	const memberIds = clusters[currentLeaderId]
	if (!memberIds) return clusters

	// Update clusters map
	delete clusters[currentLeaderId]
	clusters[photoId] = memberIds

	// Update all members' clusterLeaderIdMap to point to new leader
	updateCullingInfos((infos) => {
		for (const id of memberIds) {
			const info = allCullingInfo[id]
			if (!info) continue
			infos[id] = {
				...info,
				clusterLeaderIdMap: { ...info.clusterLeaderIdMap, [threshold]: photoId },
			}
		}
	})

	return clusters
}

/** Thresholds used by edit clustering (see getSceneThreshForTimeDiff). */
const MetaDistanceThreshold = 0.38
const LightDistanceThreshold = 0.17
const TimeDistanceThreshold = 120
const WBDistanceWeight = 0.15

function getClusteringTimeThreshold() {
	const deltaTimeThresh = store.get(deltaTimeThreshAtom) ?? 0
	return TimeDistanceThreshold + deltaTimeThresh
}

function getMetadataTimestamp(photoId: string) {
	const metadata = getPhotoMetadata(photoId)
	return metadata?.dateCreated ?? metadata?.dateModified ?? null
}

function isWithinClusteringTimeWindow(photoId1: string, photoId2: string) {
	const time1 = getMetadataTimestamp(photoId1)
	const time2 = getMetadataTimestamp(photoId2)
	if (!time1 || !time2) return false

	return Math.abs(time1 - time2) / 1000 <= getClusteringTimeThreshold()
}

function getSceneThreshForTimeDiff(secondsDiff: number): number {
	// Get delta from settings (positive = more clustering, negative = less clustering)
	// Fallback to 0 for safety in case of missing localStorage migration
	const deltaSceneThresh = store.get(deltaSceneThreshAtom) ?? 0

	// Define the threshold points
	const timePoints = [5, 10, 35, 120]
	const thresholds = [0.255, 0.245, 0.24, 0.235]

	let baseThreshold: number

	// Handle edge cases
	if (secondsDiff <= (timePoints[0] ?? 0)) {
		baseThreshold = thresholds[0] as number // Use the highest threshold for very close timestamps
	} else if (secondsDiff >= (timePoints[timePoints.length - 1] ?? 0)) {
		baseThreshold = thresholds[thresholds.length - 1] as number // Use the lowest threshold for distant timestamps
	} else {
		// Find the segment for interpolation
		baseThreshold = thresholds[thresholds.length - 1] as number // fallback
		for (let i = 0; i < timePoints.length - 1; i++) {
			if (secondsDiff >= (timePoints[i] ?? 0) && secondsDiff <= (timePoints[i + 1] ?? 0)) {
				// Linear interpolation formula: y = y1 + (x - x1) * (y2 - y1) / (x2 - x1)
				baseThreshold =
					(thresholds[i] ?? 0) +
					((secondsDiff - (timePoints[i] ?? 0)) *
						((thresholds[i + 1] ?? 0) - (thresholds[i] ?? 0))) /
						((timePoints[i + 1] ?? 0) - (timePoints[i] ?? 0))
				break
			}
		}
	}

	// Apply delta adjustment
	return baseThreshold + deltaSceneThresh
}

/**
 * Original on-the-fly clustering algorithm for AI edit mode — finds the key neighbor for a
 * given image. Now only used as fallback for photos without preview that aren't pre-clustered
 * before entering the applyStyle() queue.
 *
 * Note: changes here should also be reflected in clusterOnTheFlyForEdit() and vice versa.
 */
export function findKeyNeighbor(imageInfo: ImageInfoRecord): {
	keyID: string | null
	verySimilarNeighbor: string | null
} {
	// Get delta from settings (positive = more clustering, negative = less clustering)
	// Fallback to 0 for safety in case of missing localStorage migration
	const deltaSceneThresh = store.get(deltaSceneThreshAtom) ?? 0
	const deltaTimeThresh = store.get(deltaTimeThreshAtom) ?? 0
	const timeThresh = TimeDistanceThreshold + deltaTimeThresh

	const thisMeta = imageInfo.features.meta
	const thisTime = thisMeta.timestamp

	if (imageInfo.keyNeighborId)
		return { keyID: imageInfo.keyNeighborId, verySimilarNeighbor: null }
	if (!thisTime) return { keyID: null, verySimilarNeighbor: null }

	// check all other processed photo to find a close one as keyNeighbor
	const photos = store.get(photosAtom)
	const thisIsHidden = photos[imageInfo.imageId]?.isHidden
	const filteredImageInfos: ImageInfoRecord[] = []

	// Filter image by timestamp within timeThresh=180s of current image
	getImageInfoRecords().forEach((info) => {
		const keyID = info.keyNeighborId ?? info.imageId
		// For visible photos, skip candidates with hidden/locked photo as cluster key
		if (!thisIsHidden && (photos[keyID]?.isHidden || photos[keyID]?.kind === "locked")) return
		// Skip key photo that is not processed (e.g. reset after style switch)
		const keyImageInfo = getImageInfo(keyID)
		if (!keyImageInfo || !keyImageInfo.biases) return
		const thatTime = info.features.meta.timestamp
		if (info.biases && thatTime && Math.abs(thatTime - thisTime) / 1000 <= timeThresh) {
			filteredImageInfos.push(info)
		}
	})

	// Avoid clustering too far photos together
	const { sortedPhotos, idToIndex } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))
	const thisIndex = idToIndex[imageInfo.imageId] ?? 0

	function indexDiff(a: ImageInfoRecord) {
		return Math.abs((idToIndex[a.imageId] ?? 0) - thisIndex)
	}
	function isClusterInNeighbor(keyID: string, range: number = 5) {
		// Range is the maximum allowed gap between the current photo and the cluster.
		// 5 is determined by eyeballing, but can be adjusted in the future.
		const neighborInfos: ImageInfoRecord[] = []
		const strictNeighborInfos: ImageInfoRecord[] = []
		for (let i = -range; i <= range; i++) {
			if (i === 0) continue
			const index = thisIndex + i
			if (index >= 0 && index < sortedPhotos.length) {
				const id = sortedPhotos[index]?.id
				if (!id) continue
				const imageInfo = getImageInfo(id)
				if (imageInfo) {
					neighborInfos.push(imageInfo)
					if (i == 1 || i == -1) strictNeighborInfos.push(imageInfo)
				}
			}
		}
		const isInNeighbor = neighborInfos.some((info) => info.keyNeighborId === keyID)
		const isStrictInNeighbor = strictNeighborInfos.some((info) => info.keyNeighborId === keyID)
		return { isInNeighbor, isStrictInNeighbor }
	}

	// Sort filtered image by scene distance (namely deepCluster dist) + timeDist/1000 + indexDiff * 0.005
	type SortDistanceMap = {
		[key: string]: [number, number, number, number]
	}
	const sceneTimeDistMap: SortDistanceMap = filteredImageInfos.reduce((acc, val) => {
		const sceneD = getDeepClusterDistance(imageInfo, val)
		const timeD = Math.abs((val.features.meta.timestamp ?? thisTime) - thisTime) / 1000
		const indexD = indexDiff(val)
		const overallD = sceneD + timeD / 1000 + indexD * 0.005
		acc[val.imageId] = [sceneD, timeD, indexD, overallD]
		return acc
	}, {})
	const sortedImages = Object.entries(sceneTimeDistMap).sort((a, b) => a[1][3] - b[1][3])
	logClustering({ sortedImages })

	for (const [otherID, dists] of sortedImages) {
		if (otherID == imageInfo.imageId) {
			continue
		}

		const otherImageInfo = getImageInfo(otherID)

		if (!otherImageInfo) {
			continue
		}
		const keyID = otherImageInfo.keyNeighborId ?? otherImageInfo.imageId
		const keyInfo = getImageInfo(keyID)

		const secondsDiff = dists[1]
		const dMeta = getMetaDistance(imageInfo.features, otherImageInfo.features)
		const dWB = getWBDistance(imageInfo.features, otherImageInfo.features)
		const sceneDist = dists[0] + dWB * WBDistanceWeight // add WB distance to scene distance
		logClustering("findKeyNeighbor otherID:", otherID, { sceneDist, dMeta, dWB, secondsDiff })

		if (dMeta < MetaDistanceThreshold) {
			// Discourage mapping face photo to non-face photo
			const deltaT = getSceneThreshDelta(imageInfo, keyInfo ?? otherImageInfo)

			const overallLightDist = getOverallLightDistance(
				imageInfo.features,
				otherImageInfo.features,
			)
			const sceneThreshForTimeDiff = getSceneThreshForTimeDiff(secondsDiff)
			const dWBToKey = getWBDistance(
				imageInfo.features,
				keyInfo?.features ?? otherImageInfo.features,
			)
			const sceneDToKey =
				getDeepClusterDistance(imageInfo, keyInfo ?? otherImageInfo) +
				dWBToKey * WBDistanceWeight
			const { isStrictInNeighbor, isInNeighbor } = isClusterInNeighbor(keyID)
			const timeDiffToKey =
				Math.abs(
					thisTime -
						(keyInfo?.features.meta.timestamp ??
							otherImageInfo.features.meta.timestamp ??
							0),
				) / 1000
			const thresholdToKey = 0.29 - Math.min(timeDiffToKey / 90, 5) * 0.015 // 0.29 ~ 0.215

			logClustering({
				deltaT,
				overallLightDist,
				sceneThreshForTimeDiff,
				isStrictInNeighbor,
				isInNeighbor,
				sceneDToKey,
			})

			if (
				(overallLightDist < LightDistanceThreshold ||
					sceneDist < 0.18 + deltaSceneThresh) &&
				sceneDist < sceneThreshForTimeDiff + deltaT &&
				isInNeighbor && // only join cluster in neighbor +- 5 photos
				(sceneDist < 0.215 || sceneDToKey < thresholdToKey + deltaSceneThresh) &&
				(sceneDist < 0.18 ||
					isStrictInNeighbor ||
					sceneDToKey < Math.min(0.26, thresholdToKey) + deltaSceneThresh) // if not strict in neighbor, must have not-too-large scene distance to key
			) {
				let foundKeyNeighbor = false
				if (
					otherImageInfo.biases?.saturation !== saturationBiasBW ||
					sceneDist < sceneDThreshBW
				)
					foundKeyNeighbor = true

				if (foundKeyNeighbor) {
					logClustering("Found other image as keyNeighbor:", keyID)
					const verySimilarNeighbor =
						sceneDist < sceneDThreshForVerySimilarNeighbor &&
						otherID !== keyID &&
						overallLightDist < 0.035
							? otherID
							: null
					return { keyID, verySimilarNeighbor }
				}
			}
		}
	}

	return { keyID: null, verySimilarNeighbor: null }
}

const sceneDistMap: Record<string, Record<string, number>> = {}

/** Memoized pairwise scene distance using culling features — avoids recomputing during clustering. */
export function getSceneDist(info1: CullingInfoRecord, info2: CullingInfoRecord) {
	let val = sceneDistMap[info1.photoId]?.[info2.photoId]
	if (val != undefined) {
		return val
	} else {
		val = getDeepClusterDistanceForCulling(info1, info2)

		const map1 = sceneDistMap[info1.photoId]
		if (map1) map1[info2.photoId] = val
		else sceneDistMap[info1.photoId] = { [info2.photoId]: val }

		const map2 = sceneDistMap[info2.photoId]
		if (map2) map2[info1.photoId] = val
		else sceneDistMap[info2.photoId] = { [info1.photoId]: val }

		return val
	}
}

/** Returns photos sorted by capture time (falls back to filename before metadata is ready), plus
 * a reverse id→index map for fast proximity checks. Cached per project + photo count. */
export const getSortedPhotosAndIndexMap = cached(
	(photos: PhotoRecord[]) => {
		let sortedPhotos: PhotoRecord[]
		if (!store.get(isMetadataFinishedAtom)) {
			sortedPhotos = [...photos].sort((a, b) => a.fileName.localeCompare(b.fileName))
			logClustering(
				"Sorting photos by filename",
				photos.length,
				sortedPhotos.map((val) => val.id),
			)
		} else {
			sortedPhotos = [...photos].sort((a, b) => {
				const timeA = getPhotoMetadata(a.id)?.dateCreated
				const timeB = getPhotoMetadata(b.id)?.dateCreated
				if (timeA != undefined && timeB != undefined && timeA !== timeB)
					return timeA - timeB
				else return a.fileName.localeCompare(b.fileName)
			})
			logClustering(
				"Sorting photos by time",
				photos.length,
				sortedPhotos.map((val) => val.id),
			)
		}

		const idToIndex: { [id: string]: number } = {}
		sortedPhotos.forEach((val, index) => {
			idToIndex[val.id] = index
		})

		return { sortedPhotos, idToIndex }
	},
	(photos) =>
		`${store.get(projectAtom)?.id ?? ""}_${photos.length}_${store.get(isMetadataFinishedAtom)}`,
)

/** Check if two photos are mergeable for AI edit mode clustering. */
function isMergeableForEdit(
	cullingInfo: CullingInfoRecord,
	otherCullingInfo: CullingInfoRecord,
	allCullingInfo: Record<string, CullingInfoRecord>,
) {
	const deltaSceneThresh = store.get(deltaSceneThreshAtom) ?? 0

	const thisMetadata = getPhotoMetadata(cullingInfo.photoId)
	const otherMetadata = getPhotoMetadata(otherCullingInfo.photoId)
	const thisTime = thisMetadata?.dateCreated ?? thisMetadata?.dateModified ?? null
	const otherTime = otherMetadata?.dateCreated ?? otherMetadata?.dateModified ?? null
	if (!thisTime || !otherTime) return false
	const secondsDiff = Math.abs(thisTime - otherTime) / 1000

	const { sortedPhotos, idToIndex } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))
	const thisIndex = idToIndex[cullingInfo.photoId] ?? 0

	// Avoid clustering too far photos together
	function isClusterInNeighbor(keyID: string, range: number = 5) {
		// Range is the maximum allowed gap between the current photo and the cluster.
		// 5 is determined by eyeballing, but can be adjusted in the future.
		const neighborInfos: CullingInfoRecord[] = []
		const strictNeighborInfos: CullingInfoRecord[] = []
		for (let i = -range; i <= range; i++) {
			if (i === 0) continue
			const index = thisIndex + i
			if (index >= 0 && index < sortedPhotos.length) {
				const id = sortedPhotos[index]?.id
				if (!id) continue
				const info = allCullingInfo[id]
				if (info) {
					neighborInfos.push(info)
					if (i == 1 || i == -1) strictNeighborInfos.push(info)
				}
			}
		}
		const isInNeighbor = neighborInfos.some(
			(info) => info.clusterLeaderIdMap?.[ClusterThreshold.EditClustering] === keyID,
		)
		const isStrictInNeighbor = strictNeighborInfos.some(
			(info) => info.clusterLeaderIdMap?.[ClusterThreshold.EditClustering] === keyID,
		)
		return { isInNeighbor, isStrictInNeighbor }
	}

	const dMeta = computeMetaDistanceFromMetadata(thisMetadata, otherMetadata)
	const overallLightDist = computeLightDistanceFromCullingInfo(cullingInfo, otherCullingInfo)
	const keyID =
		otherCullingInfo.clusterLeaderIdMap?.[ClusterThreshold.EditClustering] ??
		otherCullingInfo.photoId
	const keyCullingInfo = allCullingInfo[keyID] ?? otherCullingInfo

	const dWB = getWBDistanceFromMetadata(thisMetadata, otherMetadata)
	const dWBToKey = getWBDistanceFromMetadata(
		thisMetadata,
		getPhotoMetadata(keyCullingInfo.photoId),
	)

	const sceneDist = getSceneDist(cullingInfo, otherCullingInfo) + dWB * WBDistanceWeight
	const sceneDToKey = getSceneDist(cullingInfo, keyCullingInfo) + dWBToKey * WBDistanceWeight
	const keyMetadata = getPhotoMetadata(keyCullingInfo.photoId)
	const timeDiffToKey =
		Math.abs(thisTime - (keyMetadata?.dateCreated ?? keyMetadata?.dateModified ?? 0)) / 1000
	const thresholdToKey = 0.29 - Math.min(timeDiffToKey / 90, 5) * 0.015 // 0.29 ~ 0.215

	if (
		dMeta < MetaDistanceThreshold &&
		isWithinClusteringTimeWindow(cullingInfo.photoId, otherCullingInfo.photoId)
	) {
		// Discourage mapping face photo to non-face photo
		let deltaT = 0
		const thisHasFace = (cullingInfo.faceInfo?.length ?? 0) > 0
		const keyHasFace = (keyCullingInfo?.faceInfo?.length ?? 0) > 0
		if (thisHasFace && !keyHasFace) {
			deltaT = -0.02
		}

		const sceneThreshForTimeDiff = getSceneThreshForTimeDiff(secondsDiff)
		const { isInNeighbor, isStrictInNeighbor } = isClusterInNeighbor(keyID)

		if (
			(overallLightDist < LightDistanceThreshold || sceneDist < 0.18 + deltaSceneThresh) &&
			sceneDist < sceneThreshForTimeDiff + deltaT &&
			isInNeighbor && // only join cluster in neighbor +- 5 photos
			(sceneDist < 0.215 || sceneDToKey < thresholdToKey + deltaSceneThresh) &&
			(sceneDist < 0.18 ||
				isStrictInNeighbor ||
				sceneDToKey < Math.min(0.26, thresholdToKey) + deltaSceneThresh) // if not strict in neighbor, must have not-too-large scene distance to key
		) {
			return true
		}
	}

	return false
}

/**
 * Cluster a photo for edit mode using the same logic as the old findKeyNeighbor() function
 * Works with CullingInfoRecord instead of ImageInfoRecord for efficiency
 * Updates cullingInfo.clusterLeaderIdMap[ClusterThreshold.EditClustering] and [ClusterThreshold.VerySimilar]
 * Note: changing logic here should also be reflected in the old findKeyNeighbor() function and vice versa.
 */
export function clusterOnTheFlyForEdit(
	cullingInfo: CullingInfoRecord,
	allCullingInfo: Record<string, CullingInfoRecord>,
): CullingInfoRecord {
	// Get delta from settings (positive = more clustering, negative = less clustering)
	const deltaTimeThresh = store.get(deltaTimeThreshAtom)
	const timeThresh = TimeDistanceThreshold + deltaTimeThresh

	const thisMetadata = getPhotoMetadata(cullingInfo.photoId)
	const thisTime = thisMetadata?.dateCreated ?? thisMetadata?.dateModified ?? null

	// Check if already clustered
	if (cullingInfo.clusterLeaderIdMap?.[ClusterThreshold.EditClustering]) {
		return cullingInfo
	}

	if (!thisTime) {
		return cullingInfo
	}

	// Filter candidates by timestamp within timeThresh of current photo and already clustered
	const photos = store.get(photosAtom)
	const thisIsHidden = photos[cullingInfo.photoId]?.isHidden
	const filteredCullingInfos: CullingInfoRecord[] = []
	Object.values(allCullingInfo).forEach((info) => {
		const keyID = info.clusterLeaderIdMap?.[ClusterThreshold.EditClustering]
		// For visible photos, skip candidates whose cluster leader is hidden/locked
		if (!thisIsHidden && keyID && (photos[keyID]?.isHidden || photos[keyID]?.kind === "locked"))
			return
		const thatMetadata = getPhotoMetadata(info.photoId)
		const thatTime = thatMetadata?.dateCreated ?? thatMetadata?.dateModified
		const isClustered = keyID != null

		if (isClustered && thatTime && Math.abs(thatTime - thisTime) / 1000 <= timeThresh) {
			filteredCullingInfos.push(info)
		}
	})

	const { idToIndex } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))
	const thisIndex = idToIndex[cullingInfo.photoId] ?? 0

	function indexDiff(a: CullingInfoRecord) {
		return Math.abs((idToIndex[a.photoId] ?? 0) - thisIndex)
	}

	// Sort filtered candidates by overall distance (scene + time + index)
	type SortDistanceMap = {
		[key: string]: [number, number, number, number]
	}
	const sceneTimeDistMap: SortDistanceMap = filteredCullingInfos.reduce((acc, val) => {
		const sceneD = getSceneDist(cullingInfo, val)
		const valMetadata = getPhotoMetadata(val.photoId)
		const valTime = valMetadata?.dateCreated ?? valMetadata?.dateModified ?? thisTime
		const timeD = Math.abs(valTime - thisTime) / 1000
		const indexD = indexDiff(val)
		const overallD = sceneD + timeD / 1000 + indexD * 0.005
		acc[val.photoId] = [sceneD, timeD, indexD, overallD]
		return acc
	}, {})

	const sortedCandidates = Object.entries(sceneTimeDistMap).sort((a, b) => a[1][3] - b[1][3])
	logClustering("[preClusterForEdit] sortedCandidates:", cullingInfo.photoId, {
		sortedCandidates,
	})

	for (const [otherID, dists] of sortedCandidates) {
		if (otherID === cullingInfo.photoId) {
			continue
		}

		const otherCullingInfo = allCullingInfo[otherID]
		if (!otherCullingInfo) {
			continue
		}

		if (isMergeableForEdit(cullingInfo, otherCullingInfo, allCullingInfo)) {
			const keyID =
				otherCullingInfo.clusterLeaderIdMap?.[ClusterThreshold.EditClustering] ?? otherID
			const sceneDist = dists[0]
			const overallLightDist = computeLightDistanceFromCullingInfo(
				cullingInfo,
				otherCullingInfo,
			)
			let foundKeyNeighbor = false

			// Check if other photo has BW saturation bias
			const otherImageInfo = getImageInfo(otherID)
			if (
				!otherImageInfo?.biases ||
				otherImageInfo.biases.saturation !== saturationBiasBW ||
				sceneDist < sceneDThreshBW
			) {
				foundKeyNeighbor = true
			}

			if (foundKeyNeighbor) {
				logClustering("[preClusterForEdit] Found key neighbor:", keyID)

				// Check if very similar neighbor
				const verySimilarNeighbor =
					sceneDist < sceneDThreshForVerySimilarNeighbor &&
					otherID !== keyID &&
					overallLightDist < 0.035
						? otherID
						: null

				// Update clusterLeaderIdMap
				const updatedClusterLeaderIdMap = {
					...(cullingInfo.clusterLeaderIdMap ?? {}),
					[ClusterThreshold.EditClustering]: keyID,
				}

				if (verySimilarNeighbor) {
					updatedClusterLeaderIdMap[ClusterThreshold.VerySimilar] = verySimilarNeighbor
				}

				return {
					...cullingInfo,
					clusterLeaderIdMap: updatedClusterLeaderIdMap,
				}
			}
		}
	}

	// No cluster found - use itself as keyID
	return {
		...cullingInfo,
		clusterLeaderIdMap: {
			...(cullingInfo.clusterLeaderIdMap ?? {}),
			[ClusterThreshold.EditClustering]: cullingInfo.photoId,
		},
	}
}

/**
 * Helper: Compute metadata distance from PhotoMetadataRecord
 * Uses camera, time, flash, and format. Skips temp/tint (need whiteBalance data)
 */
function computeMetaDistanceFromMetadata(
	meta1: PhotoMetadataRecord | undefined | null,
	meta2: PhotoMetadataRecord | undefined | null,
): number {
	if (!meta1 || !meta2) return 1 // Large distance if metadata is missing

	const timestamp1 = meta1.dateCreated ?? meta1.dateModified
	const timestamp2 = meta2.dateCreated ?? meta2.dateModified

	// Flash difference (using absoluteDifference logic with threshold 0.2)
	const flash1 = didFlashFire(meta1.flash)
	const flash2 = didFlashFire(meta2.flash)
	const flashDist = Math.abs(Number(flash1) - Number(flash2)) / 0.2

	// Camera difference (inline logic from cameraDifference)
	const camera1 = getCameraModel(meta1)
	const camera2 = getCameraModel(meta2)
	const cameraDist = camera1 == null || camera2 == null ? 0.5 : camera1 === camera2 ? 0 : 1

	// Format difference (inline logic from formatDifference)
	const format1 = getFileFormat(meta1.extension)
	const format2 = getFileFormat(meta2.extension)
	let formatDist = 0.2 // default
	if (format1 != null && format2 != null) {
		if (format1 === "RAW" && format2 === "RAW") formatDist = 0
		else if (format1 !== "RAW" && format2 !== "RAW") formatDist = 0
		else formatDist = 1
	}

	// Time difference (inline logic from timeDifference)
	let timeDist = 1 // default
	if (timestamp1 != null && timestamp2 != null) {
		const minuteDiff = Math.abs(timestamp1 - timestamp2) / 60000
		timeDist = Math.min(Math.max(minuteDiff - 5, 0), 35) / 35
	}

	const dWB = getWBDistanceFromMetadata(meta1, meta2)

	// Weighted sum - same as getMetaDistance
	// percentDifference(temp) + absoluteDifference(tint)/100 + flashDist + cameraDist/2 + formatDist/2 + timeDist*0.4
	const metaDist = dWB + flashDist + cameraDist / 2 + formatDist / 2 + timeDist * 0.4

	return metaDist
}

/**
 * Helper: Compute lighting distance from CullingInfoRecord
 * Uses luminanceScoreStats.percentiles same as ImageFeature.light.percentiles
 */
function computeLightDistanceFromCullingInfo(
	info1: CullingInfoRecord,
	info2: CullingInfoRecord,
): number {
	const percentiles1 = info1.luminanceScoreStats?.percentiles
	const percentiles2 = info2.luminanceScoreStats?.percentiles

	if (!percentiles1 || !percentiles2) {
		return 1 // Large distance if lighting stats are missing
	}

	// Same logic as getOverallLightDistance
	return meanAbsoluteDifference(percentiles1, percentiles2)
}

/** Entry point for incremental clustering during culling AI processing. Assigns culling
 * thresholds (Similar/Loose/Exact) by checking proximity within ±10 photos in the sorted
 * strip, then delegates to clusterOnTheFlyForEdit for edit-mode clustering. */
export function clusterOnTheFlyForCullingAndEditing(
	cullingInfo: CullingInfoRecord,
	allCullingInfo: Record<string, CullingInfoRecord>,
) {
	const { sortedPhotos } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))
	const index = sortedPhotos.findIndex((val) => val.id === cullingInfo.photoId)

	if (index < 0) return cullingInfo

	const neighborIds: string[] = []
	for (let i = 0; i < 10; i++) {
		let id = sortedPhotos[index - i]?.id
		if (id) neighborIds.push(id)

		id = sortedPhotos[index + i]?.id
		if (id) neighborIds.push(id)
	}

	const thresholds = [ClusterThreshold.Similar, ClusterThreshold.Loose, ClusterThreshold.Exact]
	const clusterLeaderIdMap = {}

	for (const clusterThreshold of thresholds) {
		let k = 0
		for (const id of neighborIds) {
			const candidate = allCullingInfo[id]

			// skip unprocessed candidate
			if (
				!candidate ||
				!candidate.clusterLeaderIdMap ||
				!candidate.clusterLeaderIdMap[clusterThreshold]
			)
				continue

			if (!isWithinClusteringTimeWindow(candidate.photoId, cullingInfo.photoId)) {
				k++
				continue
			}

			const sceneD = getSceneDist(candidate, cullingInfo)
			const deltaD = faceDistance(candidate, cullingInfo) + k * 0.003
			const distance = sceneD + deltaD
			const thresholdValue = getClusterThresholdValue(clusterThreshold)
			if (distance < thresholdValue) {
				const differentLeader =
					candidate.clusterLeaderIdMap[clusterThreshold] === candidate.photoId
						? undefined
						: allCullingInfo[candidate.clusterLeaderIdMap[clusterThreshold] ?? ""]
				let distanceToLeader = distance
				if (differentLeader) {
					const deltaD =
						faceDistance(differentLeader, cullingInfo) + clamp(k - 25, 0, 20) * 0.0025
					distanceToLeader = getSceneDist(differentLeader, cullingInfo) + deltaD
				}
				if (distanceToLeader < thresholdValue + 0.05 || distance < thresholdValue - 0.03) {
					clusterLeaderIdMap[clusterThreshold] =
						candidate.clusterLeaderIdMap[clusterThreshold]
					break
				}
			}
			k++
		}

		if (clusterLeaderIdMap[clusterThreshold] == undefined) {
			clusterLeaderIdMap[clusterThreshold] = cullingInfo.photoId
		}
	}

	cullingInfo.clusterLeaderIdMap = clusterLeaderIdMap

	cullingInfo = clusterOnTheFlyForEdit(cullingInfo, allCullingInfo)

	return cullingInfo
}

/**
 * Post-processing pass that scans up to 5 following cluster leaders in sorted order and
 * merges cluster j into cluster i when:
 * (a) for culling thresholds — leaders are not too far apart AND any cross-cluster member
 *     pair is within threshold;
 * (b) for EditClustering — any member pair satisfies isMergeableForEdit (time + scene + light
 *     criteria) with a softer leader-distance guard.
 */
export function mergeClusters(
	cullingInfos: Record<string, CullingInfoRecord>,
): Record<string, CullingInfoRecord> {
	const updatedRecords: Record<string, CullingInfoRecord> = {}

	const { idToIndex } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))

	const thresholds = [...CullingClusterThresholds, ClusterThreshold.EditClustering]
	for (const threshold of thresholds) {
		const clusters = generateCullingClusters(cullingInfos, threshold)
		const leaderIds = Object.keys(clusters)
		leaderIds.sort((a, b) => (idToIndex[a] ?? 0) - (idToIndex[b] ?? 0))
		for (let i = 0; i < leaderIds.length; i++) {
			const leaderId = leaderIds[i] as string
			if (!clusters[leaderId]) continue

			let gapSize = 0
			for (let j = i + 1; j <= i + 5; j++) {
				if (gapSize > 10) break
				const candidateLeaderId = leaderIds[j]
				if (!candidateLeaderId) break
				const candidateLeaderInfo = cullingInfos[candidateLeaderId]
				if (!candidateLeaderInfo) continue
				// Skip merging if the candidate leader is already final
				if (
					candidateLeaderInfo.clusterLeaderIdMap?.[ClusterThreshold.EditClusteringFinal]
				) {
					continue
				}
				const candidateIds = clusters[candidateLeaderId]
				if (!candidateIds) continue

				// merge cluster j to i if condition holds and delete j
				if (
					isMergable({
						cullingInfos,
						clusters,
						leaderId,
						candidateLeaderId,
						threshold,
						idToIndex,
					})
				) {
					for (const candidateId of candidateIds) {
						const info = cullingInfos[candidateId] as CullingInfoRecord
						if (info.clusterLeaderIdMap) info.clusterLeaderIdMap[threshold] = leaderId

						updatedRecords[info.photoId] = info

						clusters[leaderId]?.push(info.photoId)
					}
					delete clusters[candidateLeaderId]
					logClustering("Clusters merged!", { leaderId, candidateLeaderId, threshold })
				} else {
					gapSize += candidateIds.length
				}
			}
		}
	}

	return updatedRecords
}

export function normalizeCullingClusterRuns(
	cullingInfos: Record<string, CullingInfoRecord>,
): Record<string, CullingInfoRecord> {
	const updatedRecords: Record<string, CullingInfoRecord> = {}
	const { sortedPhotos } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))

	for (const threshold of CullingClusterThresholds) {
		const closedLeaders = new Set<string>()
		let currentOriginalLeader: string | null = null
		let currentRunLeader: string | null = null

		for (const photo of sortedPhotos) {
			const info = cullingInfos[photo.id]
			if (!info?.clusterLeaderIdMap) continue

			const originalLeader = info.clusterLeaderIdMap[threshold] ?? info.photoId

			if (originalLeader !== currentOriginalLeader) {
				if (currentOriginalLeader) {
					closedLeaders.add(currentOriginalLeader)
				}

				currentOriginalLeader = originalLeader
				currentRunLeader = closedLeaders.has(originalLeader) ? info.photoId : originalLeader

				if (currentRunLeader !== originalLeader) {
					logClustering("Split non-contiguous culling cluster run", {
						threshold,
						originalLeader,
						currentRunLeader,
					})
				}
			}

			if (currentRunLeader && info.clusterLeaderIdMap[threshold] !== currentRunLeader) {
				info.clusterLeaderIdMap[threshold] = currentRunLeader
				updatedRecords[info.photoId] = info
			}
		}
	}

	return updatedRecords
}

function isMergable(options: {
	cullingInfos: Record<string, CullingInfoRecord>
	clusters: Clusters
	leaderId: string
	candidateLeaderId: string
	threshold: ClusterThreshold
	idToIndex: Record<string, number>
}) {
	const { cullingInfos, clusters, leaderId, candidateLeaderId, threshold, idToIndex } = options
	const memberIds = clusters[leaderId]
	const candidateIds = clusters[candidateLeaderId]

	if (!memberIds || !candidateIds) return false

	const currIndex = idToIndex[leaderId] ?? 0
	const candidateIndex = idToIndex[candidateLeaderId] ?? -100
	const deltaIndex = Math.max(0, Math.abs(currIndex - candidateIndex) - 1)
	if (deltaIndex >= 30) return false

	const currLeaderInfo = cullingInfos[leaderId]
	const candidateLeaderInfo = cullingInfos[candidateLeaderId]
	if (!currLeaderInfo || !candidateLeaderInfo) return false

	const deltaD =
		faceDistance(currLeaderInfo, candidateLeaderInfo) + clamp(deltaIndex - 25, 0, 20) * 0.0025
	const leaderDist = getSceneDist(currLeaderInfo, candidateLeaderInfo) + deltaD

	const thresholdValue = getClusterThresholdValue(threshold)

	// For edit clustering, we don't simply reject merging if the key (leader) distance is too large
	if (leaderDist >= thresholdValue + 0.07 && threshold !== ClusterThreshold.EditClustering)
		return false

	for (const id of memberIds) {
		const info1 = cullingInfos[id] as CullingInfoRecord
		for (const candidateId of candidateIds) {
			const k = Math.abs((idToIndex[id] || 0) - (idToIndex[candidateId] || 0)) - 1
			if (k >= 20) continue

			const info2 = cullingInfos[candidateId] as CullingInfoRecord
			if (threshold === ClusterThreshold.EditClustering) {
				const distance = getSceneDist(info1, info2)
				if (
					isMergeableForEdit(info1, info2, cullingInfos) &&
					(leaderDist < thresholdValue + 0.05 || distance < thresholdValue - 0.03)
				) {
					return true
				}
			} else {
				const sceneD = getSceneDist(info1, info2)
				const deltaD = faceDistance(info1, info2) + Math.max(k, 0) * 0.003
				const distance = sceneD + deltaD
				if (
					isWithinClusteringTimeWindow(info1.photoId, info2.photoId) &&
					distance < thresholdValue &&
					(leaderDist < thresholdValue + 0.05 || distance < thresholdValue - 0.03)
				) {
					return true
				}
			}
		}
	}

	return false
}

function isValidFaceRect(
	rect: unknown,
): rect is { x: number; y: number; width: number; height: number } {
	if (rect == null || typeof rect !== "object") return false
	if (!("x" in rect) || !("y" in rect) || !("width" in rect) || !("height" in rect)) return false

	return (
		typeof rect.x === "number" &&
		typeof rect.y === "number" &&
		typeof rect.width === "number" &&
		typeof rect.height === "number" &&
		isFinite(rect.x) &&
		isFinite(rect.y) &&
		rect.width > 0 &&
		rect.height > 0 &&
		isFinite(rect.width) &&
		isFinite(rect.height)
	)
}

function faceDistance(info1: CullingInfoRecord, info2: CullingInfoRecord) {
	const faces1 = Array.isArray(info1.faceInfo)
		? info1.faceInfo.map((val) => val?.faceRect).filter(isValidFaceRect)
		: []
	const faces2 = Array.isArray(info2.faceInfo)
		? info2.faceInfo.map((val) => val?.faceRect).filter(isValidFaceRect)
		: []

	const countDiffBase = faces1.length === faces2.length ? 0 : 0.006
	const countDiff = Math.abs(faces1.length - faces2.length)
	const frac = countDiff > 1 ? countDiff / Math.max(faces1.length, faces2.length) : 0
	const countDiffExtra = Math.max(countDiff - 2, 0)
	const countDist =
		countDiffBase + countDiff * 0.006 + Math.max(frac - 0.2, 0) * 0.03 + countDiffExtra * 0.01

	if (faces1.length === 0 || faces2.length === 0) return countDiffBase * 2

	const faceArea1 = faces1.reduce((sum, rect) => {
		return sum + rect.width * rect.height
	}, 0)
	const faceArea2 = faces2.reduce((sum, rect) => {
		return sum + rect.width * rect.height
	}, 0)
	const areaDiff = clamp(
		Math.max(faceArea1, faceArea2) / Math.min(faceArea1, faceArea2) - 1.4,
		0,
		3,
	)

	const lessFaces = faces1.length < faces2.length ? faces1 : faces2
	const moreFaces = faces1.length < faces2.length ? faces2 : faces1

	let aveDistance = 0
	for (const face1 of moreFaces) {
		let minD = 1
		for (const face2 of lessFaces) {
			const distance = dist(face1.x, face1.y, face2.x, face2.y)
			if (distance < minD) minD = distance
		}
		aveDistance += minD
	}

	aveDistance /= Math.max(moreFaces.length, 1)
	aveDistance = Math.max(0, aveDistance - 0.1)

	return areaDiff * 0.006 + aveDistance * 0.02 + countDist
}

/** Build a cluster map from culling infos using clusterLeaderIdMap[threshold] — the culling-mode
 * analogue of generateClusters, used for Similar/Loose/Exact/EditClustering thresholds. */
export function generateCullingClusters(
	cullingInfos: Record<string, CullingInfoRecord> | CullingInfoRecord[],
	threshold: ClusterThreshold,
) {
	const clusters: Clusters = {}

	const cullingInfosArray = Array.isArray(cullingInfos)
		? cullingInfos
		: Object.values(cullingInfos)

	for (const info of cullingInfosArray) {
		const keyNeighborId = info?.clusterLeaderIdMap?.[threshold] ?? info.photoId
		clusters[keyNeighborId] = clusters[keyNeighborId] || []
		clusters[keyNeighborId]?.push(info.photoId)
	}

	return clusters
}

/** Returns total effective cluster count at a given threshold, counting unprocessed photos as
 * individual clusters so the number is always correct even mid-processing. */
export function getClusterCount(threshold: ClusterThreshold) {
	const unprocessedCount =
		Object.values(store.get(photosAtom)).length -
		Object.values(store.get(cullingInfoAtom)).length
	const clusters = generateCullingClusters(store.get(cullingInfoAtom), threshold)

	return Object.keys(clusters).length + unprocessedCount
}

/** Returns photo IDs that should be auto-rejected based on the current quality thresholds
 * (blur, face quality, exposure) as configured in culling settings. */
export function getRejectedIds(cullingInfosWithScores: CullingInfoRecord[]) {
	const blurryRejectThreshold = store.get(blurryPhotoThresholdAtom)
	const blurryRejectOn = store.get(rejectBlurryPhotosAtom)
	const faceRejectThreshold = store.get(poorFaceQualityThresholdAtom)
	const faceRejectOn = store.get(rejectPoorFaceQualityAtom)
	const lightingRejectOn = store.get(rejectPoorExposureAtom)
	const rejectsIds: string[] = []
	for (const info of cullingInfosWithScores) {
		if (
			(blurryRejectOn && (info.scoreItem?.blur ?? 5) <= blurryRejectThreshold) ||
			(faceRejectOn && info.scoreItem?.face && info.scoreItem.face <= faceRejectThreshold) ||
			(lightingRejectOn && (info.scoreItem?.lighting ?? 5) <= 1.0)
		) {
			rejectsIds.push(info.photoId)
		}
	}
	return rejectsIds
}

function getKeepersInCluster(
	sortedCluster: CullingInfoRecord[],
	count: number,
	existedKeepers: CullingInfoRecord[] = [],
	excludedKeeperSet: Set<string>,
) {
	const candidates: CullingInfoRecord[] = []
	for (const info of sortedCluster) {
		if (!excludedKeeperSet.has(info.photoId)) {
			candidates.push(info)
		}
	}

	const existedKeeperIds = existedKeepers.map((val) => val.photoId)
	const keepersInCluster: CullingInfoRecord[] = [...existedKeepers]
	for (const info of candidates) {
		if (keepersInCluster.length >= count) break

		if (existedKeeperIds.includes(info.photoId)) continue
		// No need to check if the new photo is too similar to the existing keepers
		keepersInCluster.push(info)
	}

	return keepersInCluster
}

function isClusterFinished(
	memberIds: string[],
	allCullingInfos: Record<string, CullingInfoRecord>,
	processedIds: Set<string>,
) {
	// check if scoring is finished in the cluster
	for (const id of memberIds) {
		if (!processedIds.has(id)) return false
	}

	// check if clustering itself is finished
	const { sortedPhotos, idToIndex } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))
	memberIds.sort((a, b) => (idToIndex[a] ?? 0) - (idToIndex[b] ?? 0))

	const id1 = memberIds[0] ?? ""
	const index1 = idToIndex[id1] ?? 0
	const id2 = memberIds[memberIds.length - 1] ?? ""
	const index2 = idToIndex[id2] ?? 0

	for (
		let i = Math.max(0, index1 - 30);
		i <= Math.min(index2 + 30, sortedPhotos.length - 1);
		i++
	) {
		if (!allCullingInfos[sortedPhotos[i]?.id ?? ""]) return false
	}

	return true
}

function backfillKeeperShortfall(
	keepers: CullingInfoRecord[],
	cullingInfosWithScores: CullingInfoRecord[],
	totalCount: number,
	rejectSet: Set<string>,
	hardExcludedKeeperSet: Set<string>,
	getRankScore: (info: CullingInfoRecord) => number,
) {
	const addBestAvailablePhotos = (allowAutoRejects: boolean) => {
		if (keepers.length >= totalCount) {
			return
		}

		const keeperSet = new Set(keepers.map((val) => val.photoId))
		const unselected = cullingInfosWithScores.filter((val) => {
			// Manual Candidate/Reject tags are hard exclusions; never promote them.
			if (keeperSet.has(val.photoId) || hardExcludedKeeperSet.has(val.photoId)) {
				return false
			}

			// Auto rejects are soft exclusions: avoid them first, but allow them as a last resort.
			return allowAutoRejects ? rejectSet.has(val.photoId) : !rejectSet.has(val.photoId)
		})

		unselected.sort((a, b) => {
			const scoreA = (a.scoreItem?.overallFloat ?? 0) + getRankScore(a)
			const scoreB = (b.scoreItem?.overallFloat ?? 0) + getRankScore(b)
			return scoreB - scoreA
		})

		const add = unselected.slice(0, totalCount - keepers.length)
		keepers.push(...add)
	}

	addBestAvailablePhotos(false)
	if (keepers.length < totalCount) {
		addBestAvailablePhotos(true)
	}
}

/**
 * Core incremental keeper selection algorithm. Called repeatedly as scoring
 * progresses — each call produces an updated keeper set using all scores
 * available so far.
 *
 * ### Pass 1 — Per-cluster proportional allocation
 * For each cluster, allocate `floor(size × percent) + 1` keeper slots.
 * - Already-confirmed keepers (from previous calls) are preserved.
 * - Unfinished clusters (not all members scored yet) get only 1/3 of their
 *   allocation to avoid premature commitment; the global budget is reduced
 *   accordingly.
 * - Within a cluster, keepers are picked by `getKeepersInCluster` (score-based).
 * - The last keeper in each cluster is held back as a "candidate" rather than
 *   confirmed, giving the second pass flexibility to redistribute.
 *
 * ### Pass 2 — Chunked spatial redistribution
 * If the first pass didn't fill all slots, remaining candidates are spread
 * evenly across the photo strip (sorted order). The strip is divided into
 * chunks; each chunk's local keeper density is compared to the target percent,
 * and top-scoring candidates fill any deficit. This helps distribute keepers
 * more evenly across the photo strip.
 *
 * ### Fallback
 * If keepers are still short (e.g. many rejects), unselected non-rejected photos are added by score until targetCount is met.
 */
export function getKeeperIdsOnTheFly(
	targetCount: number,
	percent: number,
	options: { excludedKeeperIds?: Set<string> } = {},
): { keeperIds: string[]; rejectIds: string[] } {
	const allCullingInfos = store.get(cullingInfoAtom)
	const cullingInfosWithScores = Object.values(allCullingInfos).filter(
		(val) => val.scoreItem != undefined,
	)
	const rankScoreById = new Map<string, number>()
	const processedIds = new Set(cullingInfosWithScores.map((val) => val.photoId))

	const clusters = generateCullingClusters(cullingInfosWithScores, ClusterThreshold.Similar)

	let totalCount = Math.min(cullingInfosWithScores.length * percent, targetCount)

	const keepers: CullingInfoRecord[] = []
	const candidates: CullingInfoRecord[] = []
	const rejectIds = getRejectedIds(cullingInfosWithScores)
	const rejectSet = new Set(rejectIds)
	const hardExcludedKeeperSet = options.excludedKeeperIds ?? new Set<string>()
	const excludedKeeperSet = new Set([...rejectSet, ...hardExcludedKeeperSet])

	for (const leaderId in clusters) {
		const memberIds: string[] = clusters[leaderId] as string[]
		const cluster = memberIds.map((val) => allCullingInfos[val] as CullingInfoRecord)

		const isFinished = isClusterFinished(memberIds, allCullingInfos, processedIds)

		let count = Math.floor(memberIds.length * percent) + 1
		const existedKeepers = cluster.filter(
			(val) =>
				val.scoreItem?.isKeeperMap?.[targetCount] === true &&
				!excludedKeeperSet.has(val.photoId),
		)

		if (!isFinished) {
			count = Math.round(count / 3) // only add 1/3 keepers if cluster is not finished
			totalCount -= (2 / 3) * memberIds.length * percent
		}

		if (existedKeepers.length >= count || count <= 0) {
			keepers.push(...existedKeepers)
			continue
		}

		cluster.sort((a, b) => {
			const scoreA = a.scoreItem?.overallFloat ?? 0
			const scoreB = b.scoreItem?.overallFloat ?? 0
			return scoreB - scoreA
		})
		let rank = 0.4
		for (const info of cluster) {
			if (info.scoreItem) {
				rankScoreById.set(
					info.photoId,
					(1 - rank / cluster.length) * (0.3 + Math.min(cluster.length / 10, 0.7)),
				)
			}
			rank += 1
		}

		const keepersInCluster = getKeepersInCluster(
			cluster,
			count,
			existedKeepers,
			excludedKeeperSet,
		)
		if (
			keepersInCluster.length <= existedKeepers.length ||
			(keepersInCluster.length < count && isFinished)
		) {
			keepers.push(...keepersInCluster)
		} else {
			keepers.push(...keepersInCluster.slice(0, -1))
			candidates.push(keepersInCluster[keepersInCluster.length - 1] as CullingInfoRecord)
		}
	}

	totalCount = Math.round(totalCount)
	const { sortedPhotos, idToIndex } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))

	if (cullingInfosWithScores.length === sortedPhotos.length && keepers.length > totalCount)
		console.warn(
			`Scoring finished, but first round keepers count ${keepers.length} is larger than totalCount ${totalCount}`,
		)
	if (cullingInfosWithScores.length === sortedPhotos.length && totalCount != targetCount)
		console.warn(
			`Scoring finished, but totalCount ${totalCount} is different from targetCount ${targetCount}`,
		)

	if (keepers.length >= totalCount) {
		// keepers include all existing keepers, and keepers.length <= targetCount, so all existing keepers are kept
		if (keepers.length > targetCount) {
			console.warn(
				`Keepers count ${keepers.length} is larger than targetCount ${targetCount}; possibly not all existing keepers are kept`,
			)
		}
		const keeperIds = keepers.slice(0, targetCount).map((val) => val.photoId)
		return { keeperIds, rejectIds }
	}

	const needCount = totalCount - keepers.length
	const needPercent = needCount / Math.max(candidates.length, 0.1)
	const keeperIdSetForCount = new Set(keepers.map((val) => val.photoId))
	const getRankScore = (info: CullingInfoRecord) =>
		rankScoreById.get(info.photoId) ?? info.scoreItem?.rankScore ?? 0
	logClustering({
		needPercent,
		totalCount,
		percent,
		keepers: keepers.length,
		candidates: candidates.length,
	})

	if (needPercent >= 1) {
		for (const candidate of candidates) {
			keepers.push(candidate)
			keeperIdSetForCount.add(candidate.photoId)
		}
	} else {
		candidates.sort((a, b) => (idToIndex[a.photoId] ?? 0) - (idToIndex[b.photoId] ?? 0))

		const aveClusterSize = cullingInfosWithScores.length / Object.keys(clusters).length
		const chunkSize = Math.max(30, Math.round(10 * aveClusterSize), Math.round(1 / percent))
		let remainingCount = cullingInfosWithScores.length
		let needCount = totalCount
		let needPercentCurr = needCount / remainingCount

		let k = 0
		for (let i = 0; i < sortedPhotos.length; i += chunkSize) {
			if (remainingCount === 0) break

			let processedCount = 0
			let keeperCount = 0
			for (let j = i; j < Math.min(i + chunkSize, sortedPhotos.length); j++) {
				const photo = sortedPhotos[j] as PhotoRecord
				const info = allCullingInfos[photo.id]
				if (info?.scoreItem) processedCount++
				if (keeperIdSetForCount.has(photo.id)) keeperCount++
			}
			if (processedCount === 0) continue

			const chunk: CullingInfoRecord[] = []
			let candidateIndex = idToIndex[candidates[k]?.photoId ?? ""]
			while (candidateIndex != undefined && candidateIndex < i + chunkSize) {
				chunk.push(candidates[k] as CullingInfoRecord)
				k++
				candidateIndex = idToIndex[candidates[k]?.photoId ?? ""]
			}

			const existedPercent = keeperCount / processedCount
			const needCandidates = Math.round((needPercentCurr - existedPercent) * processedCount)
			logClustering("Keepers candidate", {
				keeperCount,
				processedCount,
				needCandidates,
				chunk,
			})

			if (needCandidates > 0 && chunk.length > 0) {
				chunk.sort((a, b) => {
					const scoreA = (a.scoreItem?.overallFloat ?? 0) + getRankScore(a)
					const scoreB = (b.scoreItem?.overallFloat ?? 0) + getRankScore(b)
					return scoreB - scoreA
				})

				const topPercent = chunk.slice(0, needCandidates)

				keepers.push(...topPercent)
				for (const keeper of topPercent) {
					keeperIdSetForCount.add(keeper.photoId)
				}
				keeperCount += topPercent.length
			}

			if (keepers.length >= totalCount) break

			remainingCount -= processedCount
			needCount -= keeperCount
			needPercentCurr = needCount / remainingCount
		}
	}

	if (keepers.length < totalCount) {
		logClustering(
			"Keepers is less than needed:",
			keepers.length,
			totalCount,
			cullingInfosWithScores.length,
		)
		backfillKeeperShortfall(
			keepers,
			cullingInfosWithScores,
			totalCount,
			rejectSet,
			hardExcludedKeeperSet,
			getRankScore,
		)
	}

	const keeperIds = keepers.map((val) => val.photoId).slice(0, totalCount)
	return { keeperIds, rejectIds }
}

/** Applies keeper selection to the store: runs getKeeperIdsOnTheFly and writes updated
 * isKeeperMap[count] flags back to cullingInfo for each photo. */
export function selectKeepersOnTheFly(
	count: number,
	options: { excludedKeeperIds?: Set<string> } = {},
) {
	const allPhotos = store.get(allPhotosAtom)
	const percent = count / allPhotos.length
	const { keeperIds, rejectIds } = getKeeperIdsOnTheFly(count, percent, options)
	const keeperIdSet = new Set(keeperIds)
	const allCullingInfos = store.get(cullingInfoAtom)

	updateCullingInfos((infos) => {
		for (const info of Object.values(allCullingInfos)) {
			const isKeeperPrev = info.scoreItem?.isKeeperMap?.[count] ?? false
			const isKeeperNow = keeperIdSet.has(info.photoId)
			if (isKeeperPrev !== isKeeperNow && info.scoreItem) {
				const nextIsKeeperMap = {
					...(info.scoreItem.isKeeperMap ?? {}),
					[count]: isKeeperNow,
				}
				infos[info.photoId] = {
					...info,
					scoreItem: {
						...info.scoreItem,
						isKeeperMap: nextIsKeeperMap,
					},
				}
			}
		}
	}, true)

	return { keeperIds, rejectIds }
}

/**
 * Log a structured summary of the final cluster topology.
 * Useful for diagnosing adjustment inheritance bugs: who is the key photo,
 * how many members does each cluster have, and are any clusters unexpectedly large?
 */
export function logClusterSummary(options: {
	cullingInfos: Record<string, CullingInfoRecord>
	threshold: ClusterThreshold
	prefix: string
}) {
	const { cullingInfos, threshold, prefix } = options
	const clusters = generateCullingClusters(cullingInfos, threshold)
	const clusterSizes = Object.values(clusters).map((members) => members.length)
	if (clusterSizes.length === 0) return
	const total = clusterSizes.reduce((a, b) => a + b, 0)
	logClustering(`${prefix} Final cluster summary:`, {
		numClusters: clusterSizes.length,
		totalPhotosInClusters: total,
		avgClusterSize: (total / clusterSizes.length).toFixed(1),
		maxClusterSize: Math.max(...clusterSizes),
		// key photo (filename only) → member filenames; useful for tracing inheritance chains
		clusters: Object.fromEntries(
			Object.entries(clusters).map(([key, members]) => [
				key.split("|")[0],
				members.map((m) => m.split("|")[0]),
			]),
		),
	})
}
